/**
 * Apply Button Component
 * Large apply button with tracking and state management
 */

import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { reactGaEvent } from '../../../../../utils/cookie-consent/react-ga-safe';
import { trackJobApply, trackEvent } from '../../../../../utils/analytics/analytics';
import type { DisplayJob } from '../../../../../types';

export interface ApplyButtonProps {
  applyUrl: string;
  isApplied: boolean;
  displayJob: DisplayJob | null;
  applyBtnStyle: React.CSSProperties;
  applyBtnHoverStyle: React.CSSProperties;
  effectiveBrandColour: string;
  onSetApplied: (job: any, applied: boolean) => Promise<void>;
  /** Tighter spacing when placed high on the page (above the fold). */
  compact?: boolean;
  /** Second CTA after long content — uses a div wrapper so we do not duplicate a navigation landmark. */
  isSecondary?: boolean;
}

export const ApplyButton: React.FC<ApplyButtonProps> = ({
  applyUrl,
  isApplied,
  displayJob,
  applyBtnStyle,
  applyBtnHoverStyle,
  effectiveBrandColour,
  onSetApplied,
  compact = false,
  isSecondary = false,
}) => {
  const intl = useIntl();

  const handleClick = () => {
    reactGaEvent({
      category: 'interactions',
      action: 'click_apply_ad',
      label: displayJob?.objectID ?? undefined,
      nonInteraction: false,
    });

    // Track conversion with typed event
    if (displayJob) {
      trackEvent({
        name: 'job_apply_click',
        params: {
          job_id: displayJob.objectID,
          company_id: displayJob.companyId || '',
          apply_destination: 'external_link',
          locale: intl.locale,
        },
      });

      // Also call legacy function for backward compatibility
      trackJobApply({
        jobId: displayJob.objectID,
        jobTitle: displayJob.normalisedTitle || displayJob.title || '',
        companyId: displayJob.companyId || '',
        locale: intl.locale,
        method: 'external_link',
      });
    }

    if (displayJob) {
      void onSetApplied({ ...displayJob, id: displayJob.objectID }, !isApplied);
    }
  };

  const link = (
    <a
      href={applyUrl}
      className={
        'w-full px-6 rounded-2xl text-white font-semibold shadow transition-transform duration-200 flex items-center justify-center text-center hover:scale-[1.02] active:scale-[0.98] ' +
        (compact ? 'py-3.5 text-base my-0 mb-5' : 'py-4 text-lg my-6')
      }
      style={applyBtnStyle}
      target="_blank"
      rel="noopener noreferrer"
      onMouseOver={effectiveBrandColour ? (e) => Object.assign(e.currentTarget.style, applyBtnHoverStyle) : undefined}
      onMouseOut={effectiveBrandColour ? (e) => Object.assign(e.currentTarget.style, applyBtnStyle) : undefined}
      onClick={handleClick}
    >
      {isApplied ? (
        <FormattedMessage
          id="jobDetailPage.applied"
          defaultMessage="Applied ✓"
          description="Button text when job has been applied to"
        />
      ) : (
        <FormattedMessage id="jobDetailPage.applyNow" defaultMessage="Apply on Company Website" description="Apply now button on job detail page" />
      )}
    </a>
  );

  if (isSecondary) {
    return <div className="w-full">{link}</div>;
  }

  return (
    <nav
      id="job-apply-cta"
      aria-label={intl.formatMessage({ id: 'jobDetailPage.applyNavigation', defaultMessage: 'Apply to job', description: 'Accessibility label for navigation to job application page' })}
    >
      {link}
    </nav>
  );
};
